/**
 * RatingField - Star rating input (1 to maxStars, default 5)
 *
 * Task: T-P5-D4-F2-3
 * Feature: P5-D4-F2 Shared Form Contract Upgrade
 *
 * One tappable star button per star (field.maxStars, 1 to 10) inside FieldWrapper.
 * Deselection: optional fields clear to undefined, required fields keep value.
 * Container uses role="radiogroup" for accessibility.
 */

import type { RatingFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import { StarIcon } from './Icons';
import styles from './RatingField.module.css';

const DEFAULT_STARS = 5;

export function RatingField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: RatingFieldProps) {
  const handleClick = (starValue: number) => {
    if (starValue === value) {
      // Deselection: only allowed on optional fields
      if (!field.required) {
        onChange(undefined);
      }
    } else {
      onChange(starValue);
    }
  };

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      isGroup
      style={style}
    >
      <div
        className={styles.starGroup}
        role="radiogroup"
        aria-labelledby={`${field.fieldId}-label`}
      >
        {Array.from({ length: field.maxStars ?? DEFAULT_STARS }, (_, i) => i + 1).map((starValue) => {
          const isFilled = value !== undefined && starValue <= value;
          const isSelected = value === starValue;
          return (
            <button
              key={starValue}
              type="button"
              role="radio"
              className={`${styles.star} ${isFilled ? styles.filled : styles.outline}`}
              aria-label={starValue === 1 ? '1 star' : `${starValue} stars`}
              aria-checked={isSelected}
              onClick={() => handleClick(starValue)}
              onBlur={onBlur}
              style={nonInteractive ? { pointerEvents: 'none' } : undefined}
            >
              <StarIcon />
            </button>
          );
        })}
      </div>
    </FieldWrapper>
  );
}
